import { useEffect, useRef, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { isAxiosError } from 'axios'
import { AlertCircle, CheckCircle2, Loader2, LogOut, Mail, UserPlus } from 'lucide-react'
import { toast } from 'sonner'
import type { InvitePreviewResponse } from '@/lib/api/features/workspace-invites/types/UserInviteTypes'
import { acceptInvite } from '@/lib/api/features/workspace-invites/api/acceptInvite'
import { getInvitePreview } from '@/lib/api/features/workspace-invites/api/getInvitePreview'
import getWorkspaces from '@/lib/api/features/authentication/api/getWorkspaces'
import useLoginStore from '@/store/LoginStore'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useAuth } from '@/lib/auth/useAuth'

export const Route = createFileRoute('/_AuthLayout/invite/accept')({
  component: InviteAcceptancePage,
})

function InviteAcceptancePage() {
  const navigate = useNavigate()
  const auth = useAuth()
  const acceptAttempted = useRef(false)

  // Get token from URL query params
  const urlParams = new URLSearchParams(window.location.search)
  const token = urlParams.get('token')

  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isEmailMismatch, setIsEmailMismatch] = useState(false)
  const [workspaceInfo, setWorkspaceInfo] = useState<{
    name: string
    slug: string
  } | null>(null)
  const [invitePreview, setInvitePreview] = useState<InvitePreviewResponse | null>(null)

  useEffect(() => {
    // Store token for after auth redirect
    if (token) {
      sessionStorage.setItem('pendingInviteToken', token)
      localStorage.setItem('pendingInviteToken', token)
    }
  }, [token])

  useEffect(() => {
    // Fetch invite preview (unauthenticated) to show workspace/inviter info
    const effectiveToken = token || sessionStorage.getItem('pendingInviteToken')
    if (effectiveToken) {
      getInvitePreview(effectiveToken).then(setInvitePreview)
    }
  }, [token])

  useEffect(() => {
    // Check if user is authenticated and has a pending invite
    if (auth.isAuthenticated && !acceptAttempted.current) {
      const pendingToken = token || sessionStorage.getItem('pendingInviteToken')

      if (pendingToken) {
        acceptAttempted.current = true
        handleAcceptInvite(pendingToken)
      }
    }
  }, [auth.isAuthenticated])

  const handleAcceptInvite = async (inviteToken: string) => {
    setIsProcessing(true)
    setError(null)

    try {
      const result = await acceptInvite(inviteToken)

      setWorkspaceInfo({
        name: result.workspaceName,
        slug: result.workspaceSlug,
      })

      // Clear the token from storage
      sessionStorage.removeItem('pendingInviteToken')
      localStorage.removeItem('pendingInviteToken')

      toast.success(result.message)

      // Make the newly joined workspace active before navigating so that
      // LoginStore, RxDB, and the WS connection all line up with /{newSlug}
      // instead of the user's previously active workspace.
      try {
        const workspacesResp = await getWorkspaces()
        const workspaces = workspacesResp.data
        useLoginStore.getState().setWorkspaces(workspaces)
        const joined = workspaces.find((w) => w.workspaceId === result.workspaceId)
        if (joined) {
          useLoginStore.getState().switchWorkspace(joined)
        }
      } catch {
        // Non-fatal: AppLayout will repopulate the store on mount after navigate()
      }

      // Redirect to workspace after a short delay
      setTimeout(() => {
        navigate({ to: `/${result.workspaceSlug}`, replace: true })
      }, 2000)
    } catch (err) {
      let errorMessage = 'Failed to accept invitation'
      if (isAxiosError(err) && err.response?.data) {
        const data = err.response.data as string | { message?: string }
        errorMessage = typeof data === 'string' ? data : data.message ?? errorMessage
      } else if (err instanceof Error) {
        errorMessage = err.message
      }

      const emailMismatch = errorMessage.toLowerCase().includes('does not match')
      setIsEmailMismatch(emailMismatch)
      setError(errorMessage)

      // Keep token for email mismatch so user can retry after switching accounts
      if (!emailMismatch) {
        sessionStorage.removeItem('pendingInviteToken')
        localStorage.removeItem('pendingInviteToken')
      }

      toast.error(emailMismatch
        ? 'This invite was sent to a different email address'
        : errorMessage
      )
    } finally {
      setIsProcessing(false)
    }
  }

  const handleSignIn = () => {
    // Token is already in sessionStorage from the first useEffect,
    // so after auth it will redirect back here via postAuthNavigate
    window.location.href = '/auth'
  }

  // No token provided
  if (!token && !sessionStorage.getItem('pendingInviteToken')) {
    return (
      <Card className="w-full">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10">
              <AlertCircle className="h-5 w-5 text-destructive" />
            </div>
            <div>
              <CardTitle>Invalid Invitation</CardTitle>
              <CardDescription>
                No invitation token was provided
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            This page requires a valid invitation link. Please check your email
            for the invitation link and try again.
          </p>
          <Button onClick={() => navigate({ to: '/' })} className="w-full">
            Go to Home
          </Button>
        </CardContent>
      </Card>
    )
  }

  // Processing invitation
  if (isProcessing) {
    return (
      <Card className="w-full">
        <CardContent className="pt-6">
          <div className="flex flex-col items-center gap-4 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold">Processing your invitation...</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Please wait while we add you to the workspace
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }

  // Success state - invitation accepted
  if (workspaceInfo) {
    return (
      <Card className="w-full">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-100 dark:bg-green-900">
              <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <CardTitle>You're in!</CardTitle>
              <CardDescription>
                Successfully joined {workspaceInfo.name}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            Redirecting you to your new workspace...
          </p>
          <Button
            onClick={() => navigate({ to: `/${workspaceInfo.slug}` })}
            className="w-full"
          >
            Go to Workspace
          </Button>
        </CardContent>
      </Card>
    )
  }

  // Error state - email mismatch
  if (error && isEmailMismatch) {
    return (
      <Card className="w-full">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10">
              <AlertCircle className="h-5 w-5 text-destructive" />
            </div>
            <div>
              <CardTitle>Wrong Account</CardTitle>
              <CardDescription>
                This invite was sent to a different email address
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-6">
            You're currently signed in with a different email than the one this
            invitation was sent to. Please sign in with the correct account to
            accept this invitation.
          </p>
          <Button
            onClick={() => {
              window.location.href = '/logout'
            }}
            className="w-full"
            size="lg"
          >
            <LogOut className="mr-2 h-4 w-4" />
            Sign in with a different account
          </Button>
        </CardContent>
      </Card>
    )
  }

  // Error state - generic
  if (error) {
    return (
      <Card className="w-full">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10">
              <AlertCircle className="h-5 w-5 text-destructive" />
            </div>
            <div>
              <CardTitle>Unable to Accept Invitation</CardTitle>
              <CardDescription>Something went wrong</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">{error}</p>
          <div className="space-y-2">
            <Button onClick={() => navigate({ to: '/' })} className="w-full">
              Go to Home
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                acceptAttempted.current = false
                setError(null)
              }}
              className="w-full"
            >
              Try Again
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  // Not authenticated - show sign in prompt
  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
            <UserPlus className="h-5 w-5 text-primary" />
          </div>
          <div>
            <CardTitle>You're invited!</CardTitle>
            <CardDescription>
              {invitePreview
                ? `Join the ${invitePreview.workspaceName} workspace on Moly`
                : 'Sign in to accept your workspace invitation'}
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground mb-6">
          {invitePreview
            ? `${invitePreview.inviterName} has invited you to join the ${invitePreview.workspaceName} workspace. Collaborating with your team starts here.`
            : "You've been invited to join a workspace. Sign in or create an account to get started."}
        </p>
        <Button onClick={handleSignIn} className="w-full" size="lg">
          <Mail className="mr-2 h-4 w-4" />
          Join Workspace
        </Button>
      </CardContent>
    </Card>
  )
}
